import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { PostBillAPIContext } from "@app/provider";
import { routes } from "@app/routing";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { PdfView } from "@app/components/pdf";
import { useBillingPdfFile } from "@app/postBill/hooks";
import { downloadPdf } from "@app/postBill/utils/viewPdf";
import { logMsg } from "@app/utils/logMsg";
import "@app/postBill/postBill.scss";

const messages = defineMessages({
  billingStatementPdfTitle: {
    id: "billingStatementPdfTitle",
    defaultMessage: "Statement",
  },
});

const BillingStatementPdf: FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const { loadingStates, getStatementPdf, getCTSToken } =
    useContext(PostBillAPIContext);
  const statementData = location.state?.statementData ?? {};
  const backRoute = location.state?.from ?? routes.billingHistoryStatements;
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<boolean>(false);
  const ctsToken = getCTSToken?.();

  const pdfRequestUrl = useMemo(() => {
    if (!statementData) return null;

    const query = new URLSearchParams({
      bosOrderUid: statementData?.bosOrderUid,
      bosLineItemUid: statementData?.bosLineItemUid,
      ...(statementData.statementUid && {
        statementUid: statementData?.statementUid,
      }),
    });

    return `/api/1.0/postbill/statement-pdf?${query}`;
  }, [statementData]);

  const { file, pdfError } = useBillingPdfFile({
    url: pdfRequestUrl as string,
    token: ctsToken,
    authScheme: "cts-token",
  });

  const fetchPDF = async () => {
    try {
      const arrayBuffer = (await getStatementPdf(statementData)) as ArrayBuffer;
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      setPdfBlob(blob);
    } catch {
      setError(true);
      logMsg("error", "postbill.pdf", "Failed to get pdf statement");
    }
  };

  useEffect(() => {
    if (!statementData) return;
    fetchPDF();
  }, [statementData]);

  const onDownloadPdf = async () => {
    const fileName = intl.formatMessage(messages.billingStatementPdfTitle);
    downloadPdf(pdfBlob, fileName);
  };

  return (
    <div className="billing-pdf billing-statement-pdf">
      <BlueHeaderContent
        blueHeaderTitle={intl.formatMessage(messages.billingStatementPdfTitle)}
        crossHeaderRoute={backRoute}
        fullScreen
      >
        <LoadingContext isLoading={loadingStates.getStatementPdf}>
          <PdfView
            file={file}
            onDownload={onDownloadPdf}
            hasError={error || pdfError}
          />
        </LoadingContext>
      </BlueHeaderContent>
    </div>
  );
};

export default BillingStatementPdf;
